import type {
  DeclareExperiencesRequest,
  SaveComponentValueResponse,
  SubmitQuestionnaireRequest,
  SubmitQuizAttemptRequest,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as productAssessmentApi from "@/lib/api/flow-instances/product-assessment";
import { reuseKeys } from "./reuse-keys";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { invalidateSuitabilityQueries } from "./use-goals";
import { useInvalidateDynamicSync } from "./use-portfolios";

export const productAssessmentKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "product-assessment", "flow-instance-value"] as const,
  assessments: (id: string | null) =>
    ["flowInstances", id, "product-assessment", "assessments"] as const,
  productTypesInUse: (id: string | null) =>
    ["flowInstances", id, "product-assessment", "product-types-in-use"] as const,
};

export function useProductTypesInUse(id: string | null) {
  const { data } = useQuery({
    queryKey: productAssessmentKeys.productTypesInUse(id),
    queryFn: () => productAssessmentApi.getProductTypesInUse(id!),
    enabled: !!id,
  });
  return data?.productTypeIds ?? null;
}

export function useProductAssessmentAssessments(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: productAssessmentKeys.assessments(id),
    queryFn: () => productAssessmentApi.getAssessments(id!),
    enabled: !!id,
  });
  return { assessments: data ?? null, error, isLoading };
}

export function useProductAssessmentFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: productAssessmentKeys.flowInstanceValue(id),
    queryFn: () => productAssessmentApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

/**
 * Shared cache bookkeeping for every product-assessment write that returns the
 * component save response (declare, questionnaire submit). Primes the value cache
 * with the recomputed blob and invalidates the derived queries.
 */
function useAssessmentWriteHandlers(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  return {
    onSuccess: (result: SaveComponentValueResponse) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(productAssessmentKeys.flowInstanceValue(instanceId), {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // Slice 8 — D2 strict coverage check reads experiences[] from this blob.
        invalidateCompletion(instanceId);
        // Manual entry fills the component → the reuse button must hide. Refetch availability.
        queryClient.invalidateQueries({
          queryKey: reuseKeys.availability(instanceId, "product_assessment"),
        });
        // QP-185 — the save regenerated dynamic portfolios.
        invalidateDynamicSync(result.dynamicSync);
      }
    },
    onSettled: () => {
      invalidateSuitabilityQueries(queryClient);
    },
  };
}

/** QL-194 — plain per-category declaration for declaration-only types. */
export function useDeclareExperiences(instanceId: string) {
  const handlers = useAssessmentWriteHandlers(instanceId);
  return useMutation({
    mutationFn: (request: DeclareExperiencesRequest) =>
      productAssessmentApi.declareExperiences(instanceId, request),
    ...handlers,
  });
}

/** QL-194 — one category's questionnaire, submitted whole on "Done". */
export function useSubmitQuestionnaire(instanceId: string) {
  const handlers = useAssessmentWriteHandlers(instanceId);
  return useMutation({
    mutationFn: (request: SubmitQuestionnaireRequest) =>
      productAssessmentApi.submitQuestionnaire(instanceId, request),
    ...handlers,
  });
}

/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function useStartQuizAttempt(instanceId: string) {
  return useMutation({
    mutationFn: ({ productTypeId, subjectId }: { productTypeId: string; subjectId?: string }) =>
      productAssessmentApi.startQuiz(instanceId, productTypeId, subjectId),
  });
}

export function useSubmitQuizAttempt(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidateDynamicSync = useInvalidateDynamicSync();
  return useMutation({
    mutationFn: (request: SubmitQuizAttemptRequest) =>
      productAssessmentApi.submitQuiz(instanceId, request),
    onSuccess: (result) => {
      // The submit response has no value blob — refetch it so quizStates /
      // calculatedData outcomes converge, and mirror the declare mutation's
      // invalidation list (completion + reuse availability).
      queryClient.invalidateQueries({
        queryKey: productAssessmentKeys.flowInstanceValue(instanceId),
      });
      invalidateCompletion(instanceId);
      queryClient.invalidateQueries({
        queryKey: reuseKeys.availability(instanceId, "product_assessment"),
      });
      // QP-185 — the quiz outcome regenerated dynamic portfolios.
      invalidateDynamicSync(result.dynamicSync);
    },
    onSettled: () => {
      invalidateSuitabilityQueries(queryClient);
    },
  });
}
